import '../../../packages/neb-lit-components/src/components/neb-popup-header';
import '../../../packages/neb-lit-components/src/components/neb-button';
import '../../../packages/neb-lit-components/src/components/controls/neb-switch';
import '../misc/neb-icon';
import { openPopup } from '@neb/popup';
import { navigate } from '@neb/router';
import { html, css } from 'lit';

import '../../../packages/neb-lit-components/src/components/controls/neb-tab-group';
import {
  reprocessScheduledPayment,
  markAsPaid,
} from '../../../packages/neb-api-client/src/payments/scheduled-payments-api-client';
import { upsert } from '../../../packages/neb-api-client/src/settings-api-client';
import Overlay from '../../../packages/neb-lit-components/src/components/overlays/neb-overlay';
import {
  openOverlay,
  OVERLAY_KEYS,
} from '../../../packages/neb-lit-components/src/utils/overlay-constants';
import { fetchAndOpenMatchPopup } from '../../../packages/neb-lit-components/src/utils/patients';
import { openDirtyPopup } from '../../../packages/neb-popup';
import { POPUP_RENDER_KEYS } from '../../../packages/neb-popup/src/renderer-keys';
import { store } from '../../../packages/neb-redux/neb-redux-store';
import { parseDate } from '../../../packages/neb-utils/date-util';
import { centsToCurrency } from '../../../packages/neb-utils/formatters';
import { FetchService } from '../../../packages/neb-utils/services/fetch';
import {
  getNotifications,
  updateNotificationStatus,
} from '../../api-clients/notifications';
import { openError, openSuccess } from '../../store';
import { CSS_COLOR_GREY_2 } from '../../styles';

export const GLOBAL_NOTIFICATION_LOCAL_STORAGE_KEY =
  'neb-global-notification-setting-confirm-dismissed';

export const ELEMENTS = {
  header: {
    id: 'header',
  },
  tabGroup: {
    id: 'tab-group',
  },
  unreadNotifications: { selector: '[id^=unread-notification-]' },
  readNotifications: { selector: '[id^=read-notification-]' },
  markAsReadButtons: { selector: '[id^=mark-as-read-button-]' },
  markAsUnreadButtons: { selector: '[id^=mark-as-unread-button-]' },
  patientLinks: { selector: '[id^=patient-link-]' },
  goToAppointmentLinks: { selector: '[id^=go-to-appointment-link-]' },
};

export const NOTIFICATIONS_TABS = {
  UNREAD: 'unread',
  READ: 'read',
};

const NOTIFICATION_HIGHLIGHT = css`#d5f0f9`;
const NOTIFICATION_TEXT_HIGHLIGHT = css`#08aadc`;
const NOTIFICATION_TEXT_DIM = css`#939393`;

const SCROLL_THRESHOLDS = {
  BOTTOM: 100,
  BOTTOM_BUFFER: 50,
  TOP: 100,
  TOP_BUFFER: 150,
};

class NebOverlayNotifications extends Overlay {
  static get properties() {
    return {
      __userId: String,
      __patientId: String,
      __selectedTab: String,

      __unreadCount: Number,
      __readCount: Number,
      __originalUnreadCount: Number,
      __originalServerUnreadCount: Number,
      __originalServerReadCount: Number,

      __unreadNotifications: { type: Array },
      __readNotifications: { type: Array },
      __displayUnreadNotifications: { type: Array },
      __displayReadNotifications: { type: Array },
      __navItems: { type: Array },
      __markedAsReadQueue: { type: Array },
      __markedAsUnreadQueue: { type: Array },
      __originalUnreadNotifications: { type: Array },

      __isAtBottom: { type: Boolean },
      __showBackToTop: { type: Boolean },
      __markAllAsReadClicked: { type: Boolean },
      __showUndoButton: { type: Boolean },
      __isAutoFetching: { type: Boolean },
      __globalNotificationStatusChanges: { type: Boolean },
      __showSettingsMenu: { type: Boolean },
      __updatingSetting: { type: Boolean },

      __autoFetchTimeout: { type: Object },
      __settingDebounceTimeout: { type: Object },
    };
  }

  initState() {
    super.initState();

    this.__userId = '';
    this.__patientId = '';
    this.__selectedTab = NOTIFICATIONS_TABS.UNREAD;
    this.__unreadCount = 0;
    this.__readCount = 0;
    this.__unreadNotifications = [];
    this.__readNotifications = [];
    this.__displayUnreadNotifications = [];
    this.__displayReadNotifications = [];
    this.__markedAsReadQueue = [];
    this.__markedAsUnreadQueue = [];
    this.__isAtBottom = false;
    this.__showBackToTop = false;
    this.__markAllAsReadClicked = false;
    this.__showUndoButton = false;
    this.__originalUnreadNotifications = [];
    this.__originalUnreadCount = 0;
    this.__autoFetchTimeout = null;
    this.__isAutoFetching = false;
    this.__originalServerUnreadCount = 0;
    this.__originalServerReadCount = 0;
    this.__globalNotificationStatusChanges = false;
    this.__showSettingsMenu = false;
    this.__updatingSetting = false;
    this.__settingDebounceTimeout = null;
    this.__navItems = this.__genNavItems();
  }

  initHandlers() {
    super.initHandlers();
    this.handlers = {
      ...this.handlers,
      selectTab: id => {
        this.__closeSettingsMenu();
        return this.__handleTabSelect(id);
      },
      markAsRead: (e, id) => {
        this.__closeSettingsMenu();
        return this.__markNotificationStatus(
          e,
          id,
          '__unreadNotifications',
          '__markedAsReadQueue',
        );
      },
      markAsUnread: (e, id) => {
        this.__closeSettingsMenu();
        return this.__markNotificationStatus(
          e,
          id,
          '__readNotifications',
          '__markedAsUnreadQueue',
        );
      },
      markAllAsRead: () => {
        if (this.__markAllAsReadClicked || this.__unreadCount === 0) return;

        this.__closeSettingsMenu();

        this.__originalUnreadNotifications = [
          ...this.__displayUnreadNotifications,
        ];

        this.__originalUnreadCount = this.__unreadCount;

        this.__unreadNotifications = [];
        this.__displayUnreadNotifications = [];
        this.__readCount += this.__unreadCount;
        this.__unreadCount = 0;

        this.__markAllAsReadClicked = true;
        this.__showUndoButton = true;
        this.__navItems = this.__genNavItems();
      },
      undoMarkAllAsRead: () => {
        if (!this.__showUndoButton) return;

        this.__unreadNotifications = [...this.__originalUnreadNotifications];
        this.__displayUnreadNotifications = [
          ...this.__originalUnreadNotifications,
        ];

        this.__unreadCount = this.__originalUnreadCount;
        this.__readCount -= this.__originalUnreadCount;

        this.__markAllAsReadClicked = false;
        this.__showUndoButton = false;

        const originalIds = this.__originalUnreadNotifications.map(n => n.id);
        this.__markedAsReadQueue = this.__markedAsReadQueue.filter(
          id => !originalIds.includes(id),
        );

        this.__originalUnreadNotifications = [];
        this.__originalUnreadCount = 0;

        this.__navItems = this.__genNavItems();
      },
      clickPatientName: async (e, patientId, notif) => {
        e.stopPropagation();
        if (!notif || !notif.data) return;

        this.__closeSettingsMenu();

        const { accountId, appointmentId } = notif.data;

        if (patientId) {
          navigate(`/patients/${patientId}/overview/profile`);
          return;
        }

        await this.__handleReadNotification(notif);

        if (accountId && appointmentId) {
          const matchedPatientId = await this.__matchPatient({
            accountId,
            appointmentId,
          });

          if (!matchedPatientId) {
            return;
          }

          navigate(`/patients/${matchedPatientId}/overview/profile`);
        }
      },
      clickAppointmentLink: async (e, notif) => {
        e.stopPropagation();
        this.__closeSettingsMenu();

        if (!notif || !notif.data) return;

        const { appointmentId, patientId, accountId } = notif.data;

        if (!appointmentId) return;

        await this.__handleReadNotification(notif);

        if (!patientId && accountId) {
          const matchedPatientId = await this.__matchPatient({
            accountId,
            appointmentId,
          });

          if (!matchedPatientId) {
            return;
          }
        }

        await openOverlay(OVERLAY_KEYS.APPOINTMENT_PAGE, {
          appointmentId,
        });
      },
      handleScroll: e => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

        const isAtBottom = distanceFromBottom < SCROLL_THRESHOLDS.BOTTOM;
        const shouldUpdate = isAtBottom
          ? distanceFromBottom < SCROLL_THRESHOLDS.BOTTOM_BUFFER
          : distanceFromBottom > SCROLL_THRESHOLDS.TOP_BUFFER;

        if (shouldUpdate && this.__isAtBottom !== isAtBottom) {
          this.__isAtBottom = isAtBottom;
        }

        this.__showBackToTop = scrollTop > SCROLL_THRESHOLDS.TOP;

        if (isAtBottom) {
          this.__handleInfiniteScroll();
        }
      },
      scrollToTop: () => {
        const content = this.shadowRoot.querySelector('.notification-content');

        if (content) {
          content.scrollTo({ top: 0, behavior: 'smooth' });
          this.__showBackToTop = false;
        }
      },
      reprocessPayment: async (e, notif) => {
        e.stopPropagation();
        if (!notif || !notif.data) return;

        if (notif.status === 'unread') {
          await this.handlers.markAsRead({ event: null }, notif.id);
        }

        const {
          schedulePaymentId,
          paymentMethodId,
          merchantAccountId,
          patientId,
        } = notif.data;

        try {
          const res = await openPopup(
            POPUP_RENDER_KEYS.TRANSACTION_PROCESSING,
            {
              promise: reprocessScheduledPayment({
                patientId,
                scheduleId: schedulePaymentId,
                paymentMethodId,
                merchantAccountId,
                logContent: {
                  action: 'payment - reProcess - neb-overlay-notifications',
                  rawModel: {
                    patientId,
                    scheduleId: schedulePaymentId,
                    paymentMethodId,
                    merchantAccountId,
                  },
                },
              }),
            },
          );

          if (res && res.status === 'Failed') {
            store.dispatch(
              openError(
                'An error occurred when processing the Scheduled Payment',
              ),
            );
          } else {
            store.dispatch(
              openSuccess('Scheduled Payment processed successfully'),
            );
          }
        } catch (error) {
          store.dispatch(
            openError(
              'An error occurred when processing the Scheduled Payment',
            ),
          );
        }
      },
      markAsPaid: async (e, notif) => {
        e.stopPropagation();
        if (!notif || !notif.data) return;

        if (notif.status === 'unread') {
          await this.handlers.markAsRead({ event: null }, notif.id);
        }

        const { schedulePaymentId, patientId } = notif.data;

        const res = await openPopup(POPUP_RENDER_KEYS.CONFIRM, {
          title: 'Mark as Paid Manually',
          message: html`
            This will manually mark this scheduled payment as "Paid Manually".
            This will not re-process this payment, and will not affect any
            future scheduled payments in the series. <br /><br />Do you want to
            continue?
          `,
          confirmText: 'Yes',
          cancelText: 'No',
        });

        if (!res) return;

        try {
          const updatedSchedule = await markAsPaid({
            patientId,
            scheduleId: schedulePaymentId,
          });

          if (updatedSchedule.status === 'Failed') {
            store.dispatch(
              openError(
                'An error occurred when updating the Scheduled Payment',
              ),
            );
          } else {
            store.dispatch(
              openSuccess('Scheduled Payment updated successfully'),
            );
          }
        } catch (error) {
          store.dispatch(
            openError('An error occurred when updating the Scheduled Payment'),
          );
        }
      },
      openPayment: async (e, notif) => {
        e.stopPropagation();
        if (!notif || !notif.data) return;

        if (notif.status === 'unread') {
          await this.handlers.markAsRead({ event: null }, notif.id);
        }

        const {
          scheduleType,
          schedulePaymentId,
          paymentTypeId,
          paymentMethodId,
          merchantAccountId,
          amount,
          paymentName,
          patientId,
        } = notif.data;

        await openOverlay(OVERLAY_KEYS.FAILED_SCHEDULED_PAYMENT, {
          type: scheduleType,
          patientId,
          item: {
            name: paymentName,
            amount,
            paymentTypeId,
            paymentMethodId,
            merchantAccountId,
            message: '',
          },
          schedule: {
            id: schedulePaymentId,
          },
        });
      },
      toggleSettingsMenu: event => {
        event.stopPropagation();
        this.__showSettingsMenu = !this.__showSettingsMenu;
      },
      toggleGlobalSetting: event => {
        if (this.__updatingSetting) {
          return;
        }

        const checked = event.value;
        this.__globalNotificationStatusChanges = checked;

        if (this.__settingDebounceTimeout) {
          clearTimeout(this.__settingDebounceTimeout);
        }

        this.__settingDebounceTimeout = setTimeout(async () => {
          await this.__performGlobalSettingUpdate(checked);
          this.__settingDebounceTimeout = null;
        }, 500);
      },
    };
  }

  async __handleReadNotification({ status, id }) {
    if (status === 'unread') {
      await this.handlers.markAsRead({ event: null }, id);
    }
  }

  async __matchPatient({ accountId, appointmentId }) {
    const res = await fetchAndOpenMatchPopup(accountId, appointmentId);

    const data = res?.data ?? [];
    const patient = data[0] ?? {};

    if (!patient?.id) {
      return null;
    }

    this.__handleMatchingPatients(patient);
    return patient.id;
  }

  __updateNotificationsWithPatientDetails(notifications, patient) {
    return notifications.map(notification => {
      const { data } = notification;
      const { appointmentId, patientId, accountId } = data;

      if (
        appointmentId &&
        !patientId &&
        accountId &&
        patient.bookingAccountId === accountId
      ) {
        return {
          ...notification,
          data: {
            ...data,
            patientId: patient.id,
          },
        };
      }

      return notification;
    });
  }

  __handleMatchingPatients(patient) {
    const formattedUnreadNotifications =
      this.__updateNotificationsWithPatientDetails(
        this.__unreadNotifications,
        patient,
      );
    this.__unreadNotifications = formattedUnreadNotifications;
    this.__displayUnreadNotifications = [...formattedUnreadNotifications];

    const formattedReadNotifications =
      this.__updateNotificationsWithPatientDetails(
        this.__readNotifications,
        patient,
      );
    this.__readNotifications = formattedReadNotifications;
    this.__displayReadNotifications = [...formattedReadNotifications];
  }

  __handleInfiniteScroll() {
    const type =
      this.__selectedTab === NOTIFICATIONS_TABS.READ ? 'read' : 'unread';
    const service =
      type === 'read' ? this.__readFetchService : this.__unreadFetchService;
    const originalServerCount =
      type === 'read'
        ? this.__originalServerReadCount
        : this.__originalServerUnreadCount;

    if (service.getState().pageItems.length < originalServerCount) {
      this.__handleFetchMore();
    }
  }

  __getServiceTypeAndData(selectedTab) {
    const type = selectedTab === NOTIFICATIONS_TABS.READ ? 'read' : 'unread';
    const service =
      type === 'read' ? this.__readFetchService : this.__unreadFetchService;
    const notifications =
      type === 'read'
        ? this.__displayReadNotifications
        : this.__displayUnreadNotifications;
    const count = type === 'read' ? this.__readCount : this.__unreadCount;

    return { type, service, notifications, count };
  }

  __checkAndAutoFetch() {
    if (this.__autoFetchTimeout) {
      clearTimeout(this.__autoFetchTimeout);
    }

    this.__autoFetchTimeout = setTimeout(() => {
      const { notifications, count } = this.__getServiceTypeAndData(
        this.__selectedTab,
      );

      if (
        notifications.length < 10 &&
        count > notifications.length &&
        !this.__isAutoFetching
      ) {
        this.__handleInfiniteScroll();
      }

      this.__autoFetchTimeout = null;
    }, 750);
  }

  __renderNotificationCard(notification, type) {
    const isUnread = type === NOTIFICATIONS_TABS.UNREAD;
    const buttonId = `mark-as-${isUnread ? 'read' : 'unread'}-button-${notification.id}`;
    const notificationId = `${type}-notification-${notification.id}`;
    const buttonLabel = isUnread ? 'Mark as read' : 'Mark as unread';
    const buttonIcon = isUnread ? 'checkOutline' : 'notification';
    const buttonClass = isUnread ? 'unread-button' : '';

    const cardClickHandler =
      notification.type === 'payment'
        ? e => this.handlers.openPayment(e, notification)
        : e => this.handlers.clickAppointmentLink(e, notification);

    return html`
      <div
        id="${notificationId}"
        class="card-container"
        @click="${cardClickHandler}"
      >
        <div class="card">${this.__renderCardContent(notification)}</div>
        <div class="button ${buttonClass}">
          <neb-button
            id="${buttonId}"
            leadingIcon="${buttonIcon}"
            label="${buttonLabel}"
            .onClick="${e =>
              this.handlers[isUnread ? 'markAsRead' : 'markAsUnread'](
                e,
                notification.id,
              )}"
          ></neb-button>
        </div>
      </div>
    `;
  }

  __renderEmptyNotificationState(totalCount, originalServerCount) {
    if (totalCount === 0 && originalServerCount === 0) {
      return html`<div class="spacer"></div>`;
    }

    if (totalCount === 0) {
      return html`
        <div class="spacer"></div>
        <div class="end-message">You're all caught up!</div>
      `;
    }

    return html`<div class="spacer"></div>`;
  }

  __renderScrollMessage(hasReachedEnd) {
    if (hasReachedEnd) return '';
    return html`<div class="scroll-message">↓ Scroll to load more</div>`;
  }

  __renderEndMessage(hasReachedEnd) {
    if (!hasReachedEnd) return '';
    return html`<div class="end-message">You're all caught up!</div>`;
  }

  __renderBackToTop(notifications) {
    if (!(notifications.length > 5 && this.__showBackToTop)) return '';
    return html`
      <div id="backToTop" @click="${this.handlers.scrollToTop}">
        <neb-icon class="chevron-back-to-top" icon="neb:chevron"></neb-icon>
      </div>
    `;
  }

  __renderSettingsMenu() {
    if (!this.__showSettingsMenu) return '';

    return html`
      <div class="settings-menu">
        <div class="settings-menu-item">
          <span class="settings-menu-label">
            Change notification status globally
          </span>
          <neb-switch
            .on="${this.__globalNotificationStatusChanges}"
            .onChange="${this.handlers.toggleGlobalSetting}"
          ></neb-switch>
        </div>
      </div>
    `;
  }

  __renderNotificationList(notifications, type) {
    const totalCount = type === 'read' ? this.__readCount : this.__unreadCount;
    const originalServerCount =
      type === 'read'
        ? this.__originalServerReadCount
        : this.__originalServerUnreadCount;

    if (!notifications.length) {
      return this.__renderEmptyNotificationState(
        totalCount,
        originalServerCount,
      );
    }

    const hasReachedEnd =
      this[`__${type}FetchService`]?.getState()?.pageItems?.length >=
      originalServerCount;

    return html`
      ${notifications.map(n => this.__renderNotificationCard(n, type))}
      <div class="spacer"></div>
      ${this.__renderScrollMessage(hasReachedEnd)}
      ${this.__renderEndMessage(hasReachedEnd)}
      ${this.__renderBackToTop(notifications)}
    `;
  }

  __renderUnread() {
    return this.__renderNotificationList(
      this.__displayUnreadNotifications,
      NOTIFICATIONS_TABS.UNREAD,
    );
  }

  __renderRead() {
    return this.__renderNotificationList(
      this.__displayReadNotifications,
      NOTIFICATIONS_TABS.READ,
    );
  }

  async __handleMarkAllAsReadBeforeTabSwitch() {
    if (!this.__markAllAsReadClicked) return;

    try {
      await updateNotificationStatus({
        status: 'read',
        markAllAsRead: true,
        globalNotificationStatusChanges: this.__globalNotificationStatusChanges,
      });
    } catch (error) {
      console.error('Failed to mark all as read on server:', error);
      store.dispatch(openError('Failed to update notification status'));
    }
  }

  __resetMarkAllAsReadState() {
    this.__markAllAsReadClicked = false;
    this.__showUndoButton = false;
    this.__originalUnreadNotifications = [];
    this.__originalUnreadCount = 0;
  }

  async __processQueuedNotifications(id) {
    const queue =
      id === NOTIFICATIONS_TABS.READ
        ? this.__markedAsReadQueue
        : this.__markedAsUnreadQueue;
    const type = id === NOTIFICATIONS_TABS.READ ? 'read' : 'unread';

    if (queue.length > 0) {
      await this.__updateNotificationStatusTo(type);
      await this.__initFetchService(type);
    }
  }

  __initializeFetchServiceIfNeeded(type) {
    if (!this[`__${type}FetchService`]) {
      return this.__initFetchService(type);
    }
    return Promise.resolve();
  }

  __resetScrollAndUI(id) {
    this.__selectedTab = id;
    this.__showBackToTop = false;

    const contentContainer = this.shadowRoot.querySelector(
      '.notification-content',
    );

    if (contentContainer) {
      contentContainer.scrollTop = 0;
    }
  }

  async __handleTabSelect(id) {
    if (id === this.__selectedTab) return;

    await this.__handleMarkAllAsReadBeforeTabSwitch();
    this.__resetMarkAllAsReadState();
    await this.__processQueuedNotifications(id);

    const type = id === NOTIFICATIONS_TABS.READ ? 'read' : 'unread';
    await this.__initializeFetchServiceIfNeeded(type);

    this.__resetScrollAndUI(id);
  }

  async __handleMarkAllAsReadOnDismiss() {
    if (!this.__markAllAsReadClicked) return;

    try {
      await updateNotificationStatus({
        status: 'read',
        markAllAsRead: true,
        globalNotificationStatusChanges: this.__globalNotificationStatusChanges,
      });
    } catch (error) {
      console.error('Failed to mark all as read on server:', error);
      store.dispatch(openError('Failed to update notification status'));
    }
  }

  async __handleIndividualNotificationUpdates() {
    if (this.__markedAsReadQueue.length > 0) {
      await this.__updateNotificationStatusTo('read');
    }

    if (this.__markedAsUnreadQueue.length > 0) {
      await this.__updateNotificationStatusTo('unread');
    }
  }

  async dismiss(result, all = false) {
    this.__dismissAll = all;

    await this.__handleMarkAllAsReadOnDismiss();
    await this.__handleIndividualNotificationUpdates();

    if (this.isDirty && (await openDirtyPopup())) {
      this.isDirty = false;
    }

    if (!this.isDirty) {
      this.__handleDismiss(result);
    }
  }

  __markNotificationStatus({ event }, id, from, queueName) {
    if (event) event.stopPropagation();

    if (this[queueName].includes(id)) {
      return;
    }

    this[queueName].push(id);

    const displayFrom =
      from === '__unreadNotifications'
        ? '__displayUnreadNotifications'
        : '__displayReadNotifications';
    const index = this[displayFrom].findIndex(n => n.id === id);

    if (index !== -1) {
      this[displayFrom].splice(index, 1);
    }

    if (from === '__unreadNotifications') {
      this.__unreadCount--;
      this.__readCount++;
    }

    if (from === '__readNotifications') {
      this.__unreadCount++;
      this.__readCount--;
    }

    this.__navItems = this.__genNavItems();
    this.__checkAndAutoFetch();
  }

  async __initFetchService(type = 'unread') {
    if (type === 'read') {
      const client = query =>
        getNotifications({ ...query, userId: this.__userId, status: 'read' });
      this.__readNotifications = [];

      this.__readFetchService = new FetchService(
        {
          onChange: ({ _pageSize, _pageIndex, pageItems, filteredCount }) => {
            if (
              this.__readNotifications.length === 0 &&
              !this.__isAutoFetching
            ) {
              this.__readCount = filteredCount;
              this.__originalServerReadCount = filteredCount;
              this.__readNotifications = [...pageItems];
              this.__displayReadNotifications = [...pageItems];
            } else {
              const currentLength = this.__readNotifications.length;
              const newItems = pageItems.slice(currentLength);

              this.__readNotifications = [
                ...this.__readNotifications,
                ...newItems,
              ];

              this.__displayReadNotifications = [
                ...this.__displayReadNotifications,
                ...newItems,
              ];
            }
          },
        },
        client,
      );

      await this.__readFetchService.fetch();
    } else {
      const client = query =>
        getNotifications({ ...query, userId: this.__userId, status: 'unread' });
      this.__unreadNotifications = [];

      this.__unreadFetchService = new FetchService(
        {
          onChange: ({ _pageSize, _pageIndex, pageItems, filteredCount }) => {
            if (
              this.__unreadNotifications.length === 0 &&
              !this.__isAutoFetching
            ) {
              this.__unreadCount = filteredCount;
              this.__originalServerUnreadCount = filteredCount;
              this.__unreadNotifications = [...pageItems];
              this.__displayUnreadNotifications = [...pageItems];
            } else {
              const currentLength = this.__unreadNotifications.length;
              const newItems = pageItems.slice(currentLength);

              this.__unreadNotifications = [
                ...this.__unreadNotifications,
                ...newItems,
              ];

              this.__displayUnreadNotifications = [
                ...this.__displayUnreadNotifications,
                ...newItems,
              ];
            }
            this.__navItems = this.__genNavItems();
          },
        },
        client,
      );

      await this.__unreadFetchService.fetch();
    }
  }

  async __updateNotificationStatusTo(status, notificationIds = null) {
    try {
      const idsToUpdate =
        notificationIds ||
        (status === 'read'
          ? this.__markedAsReadQueue
          : this.__markedAsUnreadQueue);

      await updateNotificationStatus({
        notificationUserIds: idsToUpdate,
        status,
        globalNotificationStatusChanges: this.__globalNotificationStatusChanges,
      });

      if (!notificationIds) {
        if (status === 'read') {
          this.__markedAsReadQueue = [];
        } else {
          this.__markedAsUnreadQueue = [];
        }
      }
    } catch (e) {
      console.error('Failed to update notification status:', e);
      store.dispatch(openError('Failed to update notification status'));
    }
  }

  __genNavItems() {
    const unreadLabel =
      this.__unreadCount > 0 ? `Unread (${this.__unreadCount})` : 'Unread';

    return [
      {
        id: NOTIFICATIONS_TABS.UNREAD,
        exact: false,
        label: unreadLabel,
        name: NOTIFICATIONS_TABS.UNREAD,
        icon: 'neb:notification_important',
        resolver: () => this.__renderUnread(),
      },
      {
        id: NOTIFICATIONS_TABS.READ,
        exact: false,
        label: 'Read',
        name: NOTIFICATIONS_TABS.READ,
        icon: 'neb:checked',
        resolver: () => this.__renderRead(),
      },
    ];
  }

  static get styles() {
    return [
      super.styles,
      css`
        .header {
          padding: 15px;
          background-color: ${NOTIFICATION_HIGHLIGHT};
        }

        neb-popup-header::part(close-icon) {
          fill: ${NOTIFICATION_TEXT_HIGHLIGHT};
        }

        neb-popup-header::part(container) {
          background-color: ${NOTIFICATION_HIGHLIGHT};
        }

        neb-popup-header::part(title) {
          color: ${NOTIFICATION_TEXT_HIGHLIGHT};
        }

        .link {
          cursor: pointer;
          color: ${NOTIFICATION_TEXT_HIGHLIGHT};
          text-decoration: none;
          text-shadow: 0.35px 0 0 currentColor;
          transition: color 200ms ease-out;
        }

        .link:hover {
          color: #0a5f7a;
        }

        .notification-content {
          overflow-y: auto;
          height: 600px;
        }

        .content {
          width: 635px;
          height: 800px;
        }

        .tabs {
          display: flex;
        }

        .tab-group {
          width: 100%;
          padding-top: 20px;
        }

        .tabs-container {
          display: flex;
          align-items: center;
          flex: 1;
        }

        .action-buttons {
          display: flex;
          align-items: center;
          gap: 10px;
          padding-right: 15px;
          padding-top: 5px;
          padding-bottom: 4px;
        }

        .header-actions-container {
          display: flex;
          align-items: center;
          border-bottom: 1px solid ${CSS_COLOR_GREY_2};
        }

        .mark-all-read-button neb-button::part(button) {
          background-color: ${NOTIFICATION_HIGHLIGHT};
          border: 1px solid ${NOTIFICATION_TEXT_HIGHLIGHT};
        }

        neb-tab-group::part(tab-icon-1) {
          height: 15px;
          width: 15px;
          align-self: center;
        }

        neb-tab-group::part(tab-label-container-1) {
          gap: 8px;
          padding-bottom: 1px;
        }

        .card-container {
          display: grid;
          align-items: center;
          grid-template-columns: 1fr auto;
          border-bottom: 1px solid ${CSS_COLOR_GREY_2};
          transition:
            opacity 300ms ease-out,
            background-color 200ms ease-out;
          cursor: pointer;
        }

        .card-container:hover {
          background-color: rgba(213, 240, 249, 0.3);
        }

        .card {
          padding: 15px 10px 15px 15px;
          width: 100%;
        }

        .button {
          align-items: center;
          padding: 15px 15px 15px 10px;
        }

        neb-button::part(button) {
          text-transform: none;
        }

        .appointment-date {
          white-space: nowrap;
        }

        .button neb-button::part(label),
        .mark-all-read-button neb-button::part(label) {
          text-transform: none;
          font-size: 14px;
        }

        .button neb-button::part(label-container) {
          padding: 0 10px;
        }

        .unread-button neb-button::part(button),
        .mark-all-read-button neb-button::part(button) {
          background-color: ${NOTIFICATION_HIGHLIGHT};
        }

        .unread-button neb-button::part(label),
        .mark-all-read-button neb-button::part(label) {
          color: ${NOTIFICATION_TEXT_HIGHLIGHT};
        }

        .unread-button neb-button::part(leading-icon),
        .mark-all-read-button neb-button::part(leading-icon) {
          fill: ${NOTIFICATION_TEXT_HIGHLIGHT};
        }

        .unread-button neb-button:hover::part(button),
        .mark-all-read-button neb-button:hover::part(button) {
          opacity: 1;
          background-color: #b8dce9;
        }

        .unread-button neb-button:hover::part(label),
        .mark-all-read-button neb-button:hover::part(label) {
          color: #3cc4ec;
        }

        .unread-button neb-button:hover::part(leading-icon),
        .mark-all-read-button neb-button:hover::part(leading-icon) {
          fill: #3cc4ec;
        }

        .sub-text {
          font-size: 12px;
        }

        .created-at {
          text-decoration: italic;
          color: ${NOTIFICATION_TEXT_DIM};
          padding-bottom: 4px;
        }

        .payment-header {
          padding-bottom: 4px;
        }

        .appointment-link {
          padding-top: 10px;
        }

        .appointment-descriptor {
          font-size: 12px;
        }

        .payment-title {
          font-size: 13px;
          font-weight: bold;
        }

        .appointment-booked-header {
          font-weight: bold;
          color: black;
          font-size: 13px;
        }

        .payment-actions {
          display: flex;
          gap: 15px;
        }

        .spacer {
          height: 100px;
        }

        .end-message {
          text-align: center;
          color: ${NOTIFICATION_TEXT_DIM};
          padding: 20px 0;
          font-size: 14px;
          position: relative;
        }

        .scroll-message {
          text-align: center;
          color: ${NOTIFICATION_TEXT_DIM};
          padding: 20px 0;
          font-size: 14px;
          position: absolute;
          bottom: 20px;
          left: 0;
          right: 0;
          transition: opacity 0.3s ease-out;
        }

        #backToTop {
          position: absolute;
          bottom: 20px;
          left: 20px;
          opacity: 0.7;
          transition: opacity 0.3s;
          background-color: ${NOTIFICATION_HIGHLIGHT};
          cursor: pointer;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        #backToTop:hover {
          opacity: 1;
        }

        .chevron-back-to-top {
          fill: ${NOTIFICATION_TEXT_HIGHLIGHT};
          width: 20px;
          height: 20px;
          transform: rotate(180deg);
        }

        .new-account-prefix {
          font-weight: bold;
        }

        .settings-container {
          position: relative;
          display: flex;
          align-items: center;
          padding-right: 15px;
        }

        .settings-icon {
          cursor: pointer;
          fill: ${NOTIFICATION_TEXT_HIGHLIGHT};
          width: 30px;
          height: 30px;
          border-radius: 3px;
          transition: background-color 200ms ease-out;
        }

        .settings-icon:hover {
          background-color: rgba(213, 240, 249, 0.5);
        }

        .settings-menu {
          position: absolute;
          top: 35px;
          right: 0;
          background: white;
          border: 1px solid ${CSS_COLOR_GREY_2};
          border-radius: 4px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          min-width: 250px;
        }

        .settings-menu-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 16px;
        }

        .settings-menu-label {
          font-size: 14px;
          color: #333;
        }
      `,
    ];
  }

  updated(changedProps) {
    if (super.updated) {
      super.updated(changedProps);
    }

    if (changedProps.has('model') && this.model) {
      this.__userId = this.model.userId;
      this.__patientId = this.model.patientId;
      this.__globalNotificationStatusChanges =
        this.model.globalNotificationStatusChanges || false;

      this.__initFetchService('unread');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.__autoFetchTimeout) {
      clearTimeout(this.__autoFetchTimeout);
      this.__autoFetchTimeout = null;
    }

    if (this.__settingDebounceTimeout) {
      clearTimeout(this.__settingDebounceTimeout);
      this.__settingDebounceTimeout = null;
    }
  }

  __renderCardContent(notif) {
    if (notif.type === 'payment') {
      const {
        data: {
          patientName,
          amount,
          scheduledDate,
          patientId,
          scheduleType,
          frequency,
        },
        createdAt,
      } = notif;
      const headerFormattedDate = this.__getFormattedDate(createdAt);
      const scheduleFormattedDate = this.__getDateInMMDDYYYY(scheduledDate);

      let paymentMessage;

      if (scheduleType === 'recurringPayment') {
        paymentMessage = `${centsToCurrency(amount)} ${frequency} subscription scheduled for ${scheduleFormattedDate} failed`;
      } else if (scheduleType === 'singlePayment') {
        paymentMessage = `${centsToCurrency(amount)} payment scheduled for ${scheduleFormattedDate} failed`;
      } else {
        paymentMessage = `${centsToCurrency(amount)} payment scheduled for ${scheduleFormattedDate} failed`;
      }

      return html`
        <div class="message">
          <div class="payment-header">
            <span class="payment-title">Subscription Payment Failed</span>
            <span class="created-at sub-text">${headerFormattedDate}</span>
          </div>
          <div class="sub-text">
            <span
              class="link"
              @click="${e =>
                this.handlers.clickPatientName(e, patientId, notif)}"
              >${patientName}</span
            >'s ${paymentMessage}
          </div>
          <div class="payment-actions">
            <span
              class="sub-text link appointment-link"
              @click="${e => this.handlers.reprocessPayment(e, notif)}"
            >
              Re-process Payment
            </span>
            <span
              class="sub-text link appointment-link"
              @click="${e => this.handlers.markAsPaid(e, notif)}"
            >
              Mark as Paid
            </span>
          </div>
        </div>
      `;
    }
    const {
      data: { patientName, appointmentDate, patientId },
      createdAt,
    } = notif;
    const formattedDate = this.__getFormattedDate(createdAt);
    const formattedAppointmentDate = this.__getDateInMMDDYYYY(appointmentDate);
    const patientLinkId = `patient-link-${notif.status}-${notif.id}`;
    const goToAppointmentLinkId = `go-to-appointment-link-${notif.status}-${notif.id}`;
    const isNewAccount = !patientId;

    return html`
      <div class="created-at sub-text">
        <span class="appointment-booked-header">Appointment Booked</span>
        <span class="appointment-date">${formattedDate}</span>
      </div>
      <div class="message">
        ${isNewAccount
          ? html`<span class="new-account-prefix">New Online Account: </span>`
          : ''}
        <div class="appointment-descriptor">
          <span
            id="${patientLinkId}"
            class="link"
            @click="${e => this.handlers.clickPatientName(e, patientId, notif)}"
            >${patientName}</span
          >
          booked an appointment online for
          <span class="appointment-date">${formattedAppointmentDate}</span>
        </div>
      </div>
      <div
        id="${goToAppointmentLinkId}"
        class="sub-text link appointment-link"
        @click="${e => this.handlers.clickAppointmentLink(e, notif)}"
      >
        Open appointment
      </div>
    `;
  }

  __getFormattedDate(dateToFormat) {
    const today = parseDate();
    const yesterday = parseDate().subtract(1, 'day');

    const todayDate = this.__resetTimeComponents(today);
    const yesterdayDate = this.__resetTimeComponents(yesterday);
    const notificationDate = this.__resetTimeComponents(
      parseDate(dateToFormat),
    );

    if (notificationDate.isSame(todayDate, 'day')) {
      return `Today ${parseDate(dateToFormat).format('h:mm A')}`;
    }

    if (notificationDate.isSame(yesterdayDate, 'day')) {
      return 'Yesterday';
    }
    return parseDate(dateToFormat).format('M/D/YYYY');
  }

  __resetTimeComponents(date) {
    return parseDate(date).startOf('day');
  }

  __getDateInMMDDYYYY(dateToFormat) {
    return parseDate(dateToFormat).format('MM/DD/YYYY');
  }

  __renderTabContent() {
    const item = this.__navItems.find(({ id }) => id === this.__selectedTab);
    return item.resolver();
  }

  async __handleFetchMore() {
    const type =
      this.__selectedTab === NOTIFICATIONS_TABS.READ ? 'read' : 'unread';
    const service =
      type === 'read' ? this.__readFetchService : this.__unreadFetchService;

    this.__isAutoFetching = true;

    try {
      await service.fetchMore();
    } catch (error) {
      console.error('Error fetching more notifications:', error);
    } finally {
      this.__isAutoFetching = false;
    }
  }

  async __showGlobalSettingConfirmation() {
    const result = await openPopup(POPUP_RENDER_KEYS.CONFIRM, {
      title: 'Global Notification Setting',
      message: html`
        <div style="margin-bottom: 15px;">
          This setting controls how notification status changes affect other
          users.
        </div>
        <div style="margin-bottom: 10px;">
          <strong>Enabled:</strong> Marking a notification as read or unread
          will apply to all users.
        </div>
        <div style="margin-bottom: 15px;">
          <strong>Disabled:</strong> Notification status changes only affect the
          current, logged in user.
        </div>
        <div>Do you wish to continue?</div>
      `,
      confirmText: 'Continue',
      cancelText: 'Cancel',
      checkBoxLabel: 'Do not show this message again',
    });

    if (result?.checked) {
      localStorage.setItem(GLOBAL_NOTIFICATION_LOCAL_STORAGE_KEY, 'true');
    }

    return result?.result;
  }

  async __performGlobalSettingUpdate(checked) {
    const hasBeenDismissed =
      localStorage.getItem(GLOBAL_NOTIFICATION_LOCAL_STORAGE_KEY) === 'true';

    if (!hasBeenDismissed) {
      const confirmed = await this.__showGlobalSettingConfirmation();

      if (!confirmed) {
        this.__globalNotificationStatusChanges = !checked;
        return;
      }
    }

    this.__updatingSetting = true;

    try {
      await upsert({
        globalNotificationStatusChanges: checked,
      });

      store.dispatch(
        openSuccess(
          `Global notification setting ${checked ? 'enabled' : 'disabled'} successfully`,
        ),
      );
    } catch (error) {
      console.error('Error updating global notification setting:', error);
      store.dispatch(
        openError(
          'Failed to update global notification setting. Please try again.',
        ),
      );

      this.__globalNotificationStatusChanges = !checked;
    } finally {
      this.__updatingSetting = false;
    }
  }

  renderContent() {
    return html`
      <neb-popup-header
        id="${ELEMENTS.header.id}"
        class="header"
        title="Notification Management"
        .onCancel="${this.handlers.dismiss}"
      ></neb-popup-header>

      <div class="tabs">
        <div class="tabs-container">
          <neb-tab-group
            id="${ELEMENTS.tabGroup.id}"
            class="tab-group"
            .selectedId="${this.__selectedTab}"
            .items="${this.__navItems}"
            .onSelect="${this.handlers.selectTab}"
          ></neb-tab-group>
        </div>

        <div class="header-actions-container">
          <div class="settings-container">
            <neb-icon
              class="settings-icon"
              icon="neb:settings"
              @click="${e => this.handlers.toggleSettingsMenu(e)}"
            ></neb-icon>
            ${this.__renderSettingsMenu()}
          </div>

          ${this.__selectedTab === NOTIFICATIONS_TABS.UNREAD
            ? html`
                <div class="action-buttons mark-all-read-button">
                  <neb-button
                    label="Mark all as read"
                    size="small"
                    .disabled="${this.__markAllAsReadClicked ||
                    this.__unreadCount === 0}"
                    .onClick="${this.handlers.markAllAsRead}"
                  ></neb-button>

                  ${this.__showUndoButton
                    ? html`
                        <neb-button
                          class="undo-button"
                          label="Undo"
                          size="small"
                          .onClick="${this.handlers.undoMarkAllAsRead}"
                        ></neb-button>
                      `
                    : ''}
                </div>
              `
            : ''}
        </div>
      </div>

      <div class="notification-content" @scroll="${this.handlers.handleScroll}">
        ${this.__renderTabContent()}
      </div>
    `;
  }

  __closeSettingsMenu() {
    this.__showSettingsMenu = false;
  }
}

customElements.define('neb-overlay-notifications', NebOverlayNotifications);
